import type {
  ContractRecord,
  ContractRecordMap,
  PlannableOverlapExtended,
  RestRecord
} from '@libs/data-access/entities'
import { CounterType } from '@libs/data-access/entities'
import { BALANCE_TYPE, restCounterService } from '@libs/data-access/queries'
import { Alert, Flex, Loader, Text, usePrevious } from '@libs/ui/ds'
import { usePlanningFormTranslation } from '@libs/utils/translations'
import type { RestItem } from '@spa/constants/RestTypes'
import type { ReduxState } from '@spa/redux/ReduxState'
import { useMemoSelector } from '@spa/redux/hooks'
import { RestCounterType } from 'libs/data-access/queries/src/lib/queries/RestCounter/types/RestCounter'
import React, { useCallback, useEffect, useState } from 'react'
import type { FC } from 'react'
import { useAccountFeatureFlags } from 'snap-redux/modules/accounts/hooks'
import { getMembershipById } from 'snap-redux/modules/memberships/selectors'

import type { RestFormData } from '../../../hooks'
import { NegativeCompensatoryRest } from '../Alerts'
import { DayFields } from '../DayFields/DayFields'
import { HourFields } from '../HourFields/HourFields'

import { BalanceTable } from './components/BalanceTable'

type Props = {
  rest: RestRecord
  isLoading: boolean
  restCantBeEdited: boolean
  formData: RestFormData
  restIsLocked: boolean
  selectedContract: ContractRecord
  selectedRest: RestItem
  setFormDisabled: Function
  plannableOverlapExtended: PlannableOverlapExtended
  contracts: ContractRecordMap
  disableCustomRest: boolean
  defaultRestDurationInDays: number
}
export const CounterWithdrawalForm: FC<Props> = ({
  rest,
  isLoading,
  restCantBeEdited,
  formData,
  restIsLocked,
  selectedContract,
  selectedRest,
  selectedRest: { counter_type: counterType },
  setFormDisabled,
  plannableOverlapExtended,
  contracts,
  disableCustomRest,
  defaultRestDurationInDays
}) => {
  const { t } = usePlanningFormTranslation()
  const { momentStart, momentEnd } = formData

  const accountFF = useAccountFeatureFlags()
  const restId = rest.id || null

  const startHourFormValue = formData.time_range[0]
  const endHourFormValue = formData.time_range[1]
  const [startH, startM] = startHourFormValue.split(':')
  const [endH, endM] = endHourFormValue.split(':')
  const startHour = momentStart
    .clone()
    .hours(parseInt(startH, 10))
    .minutes(parseInt(startM, 10))
  const endHour = momentEnd
    .clone()
    .hours(parseInt(endH, 10))
    .minutes(parseInt(endM, 10))

  const restDurationFormValue =
    selectedRest.id === RestCounterType.RTT
      ? defaultRestDurationInDays
      : endHour.diff(startHour, 'minutes')

  const membership = useMemoSelector(state =>
    getMembershipById(state, selectedContract.membership_id)
  )

  const locationByContract = useMemoSelector((state: ReduxState) =>
    state.locations.getIn(['data', selectedContract.location_id])
  )
  const canHaveNegativeFunds =
    locationByContract?.allow_negative_compensatory_counter ||
    selectedRest.id === RestCounterType.RTT

  /**
   * RTT COUNTER
   */
  const rttAccumulatorContractOperationsQuery =
    restCounterService.accumulators.userContracts.useFetchContractOperations(
      {
        pathParams: {
          type: RestCounterType.RTT,
          user_contract_id: selectedContract.id
        }
      },
      {
        enabled:
          accountFF?.enable_rtt && selectedRest.id === RestCounterType.RTT,
        initialData: { operations: { meta: {}, resources: [] } } as any
      }
    )

  /**
   * PUBLIC HOLIDAY COUNTER
   */
  const holidaysAccumulatorContractOperationsQuery =
    restCounterService.accumulators.userContracts.useFetchContractOperations(
      {
        pathParams: {
          type: RestCounterType.HOLIDAYS,
          user_contract_id: selectedContract.id
        }
      },
      {
        enabled: counterType === CounterType.PUBLIC_HOLIDAY,
        initialData: { operations: { meta: {}, resources: [] } } as any
      }
    )

  /**
   * RCR COUNTER
   */
  const rcrAccumulatorContractOperationsQuery =
    restCounterService.accumulators.userContracts.useFetchContractOperations(
      {
        pathParams: {
          type: RestCounterType.RCR,
          user_contract_id: selectedContract.id
        }
      },
      {
        enabled: counterType === CounterType.COMPENSATORY,
        initialData: { operations: { meta: {}, resources: [] } } as any
      }
    )

  /**
   * TOTAL BALANCE
   */
  let totalBalance = 0
  if (counterType === CounterType.PUBLIC_HOLIDAY) {
    totalBalance =
      holidaysAccumulatorContractOperationsQuery?.data?.balance_in_minutes || 0
  } else if (counterType === CounterType.COMPENSATORY) {
    totalBalance =
      rcrAccumulatorContractOperationsQuery?.data?.balance_in_minutes || 0
  } else if (selectedRest.id === RestCounterType.RTT) {
    totalBalance =
      rttAccumulatorContractOperationsQuery?.data?.balance_in_days || 0
  }

  const [notEnoughFunds, setNotEnoughFunds] = useState<boolean | null>(null)

  const getFundsStatus = useCallback(() => {
    if (canHaveNegativeFunds) {
      setNotEnoughFunds(false)
      return
    }

    if (!restId) {
      // without rest id thus no withdrawal associated we simply compare the actual counter balance with rest duration
      setNotEnoughFunds(totalBalance < restDurationFormValue)
    }
  }, [canHaveNegativeFunds, totalBalance, restDurationFormValue, restId])

  useEffect(() => {
    setNotEnoughFunds(!(canHaveNegativeFunds || restId))
    getFundsStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    getFundsStatus()
  }, [totalBalance, getFundsStatus])

  const prevSelectedRest = usePrevious(selectedRest)
  useEffect(() => {
    // If time changes, we evaluate funds. When withdrawalCounter is fetched we check the funds,
    // we send the fetchedCounterBalance to avoid falsy result
    if (prevSelectedRest) {
      getFundsStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startHourFormValue, endHourFormValue])

  useEffect(() => {
    // we listen to funds status based on getFundsStatus then update it in RestForm
    setFormDisabled(notEnoughFunds)
    return () => setFormDisabled(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notEnoughFunds])

  return (
    <Loader.Wrap loading={!restCantBeEdited && isLoading}>
      <Flex className='gap-2'>
        {selectedRest.type === 'hour' && (
          <HourFields disabled={restIsLocked || restCantBeEdited} />
        )}
        {selectedRest.type === 'day' && (
          <DayFields
            plannableOverlapExtended={plannableOverlapExtended}
            contracts={contracts}
            restIsLocked={restIsLocked}
            disableCustomRest={disableCustomRest}
            disabled={restIsLocked || restCantBeEdited}
          />
        )}

        {restCantBeEdited && (
          <Alert intent='semantic.orange' className='my-2'>
            <Text>{t('restForm.restCannotBeEdit')}</Text>
            <Text>{t('restForm.restCannotBeEditHelp')}</Text>
          </Alert>
        )}
        {notEnoughFunds && counterType && (
          <NegativeCompensatoryRest counterType={counterType} />
        )}
        {!restCantBeEdited && !isLoading && (
          <Loader.Wrap
            loading={
              rcrAccumulatorContractOperationsQuery.isLoading ||
              holidaysAccumulatorContractOperationsQuery.isLoading
            }
          >
            <BalanceTable
              membershipFirstname={membership?.firstname}
              totalBalance={totalBalance}
              restDurationFormValue={restDurationFormValue}
              balanceType={
                selectedRest.type === 'day'
                  ? BALANCE_TYPE.DAYS
                  : BALANCE_TYPE.HOURS
              }
            />
          </Loader.Wrap>
        )}
      </Flex>
    </Loader.Wrap>
  )
}
